import angular from 'angular';
import initialSetupTemplate from './initialSetup.html';
import './initialSetup.scss';

/*******************************************************************************
 * @ngdoc    component
 * @name     csm.books.InitialSetup.component:initialSetup
 * @description
 * Initial setup main application component. Shows a consecutive series of page
 * views configured for the application in `**AppConfig**`. **Next/Previous**
 * buttons at the bottom of the page traverse the views. When the last view is
 * reached, **Next** changes to **Finish** which will build the setup journal
 * entries, save them to the server, and redirect to the main menu.
 *
 * **Page Views**
 *
 * This application does not use `$route` to handle page view changes. Page views
 * are compiled using `ng-repeat` where only the view identified by `pageIndex`
 * is currently shown. A page view `id` is loaded into `$location.path()` by the
 * **Next/Previous** buttons. A watcher identifies the new view by the `id` and
 * changes the current `pageIndex`. The `$location.path()` watcher correctly
 * handles **Back/Forward** buttons and page reloads, as well.
 *
 * Each view has an optional `condition` property containing a function called
 * with `$injector.invoke()`. If this property exists, the function must return
 * `true` to show the view. Otherwise, the view is skipped.
 *
 * Views may also define `$ctrl` as a object with properties to bind to the
 * controller when the view is shown. These properties are then available to the
 * transcluded `pageHeading` element.
 *
 * **Validation**
 *
 * **Next** button validates the page view and saves the setup information to the
 * server before transitioning to the next page. Validators are defined inside
 * page view and passed back to this component by calling `addValidator()`.
 *
 * **`pageHeading`**
 *
 * Transclusion slot for page heading content. `$ctrl` is injected into the
 * transcluded content.
 *******************************************************************************/
export const initialSetupComponent = {
  template: initialSetupTemplate,
  transclude: { pageHeading: '?pageHeading' },
  controller: controller
};

/*******************************************************************************
 * controller
 *******************************************************************************/
/* @ngInject */ function controller(
  $element,
  $injector,
  $location,
  $rootScope,
  $scope,
  $transclude,
  AppData,
  AppServices,
  CompilerService,
  BusyScreen
) {
  var $svcs = { $ctrl: this, $scope: $scope };
  var $ctrl = $injector.invoke(
    init$ctrl,
    null,
    Object.assign($svcs, { $svcs: $svcs })
  );

  /*------------------------------------------------------------------*/
  /* Insert transcluded content and add reference to this controller. */
  /*------------------------------------------------------------------*/
  CompilerService.transcludeSlots($element, $svcs, $transclude);

  /*-------------------------------------------------------*/
  /* Load setup info from server to controller properties. */
  /*-------------------------------------------------------*/
  BusyScreen.setBusy(true);

  AppServices.loadSetup().then(function (data) {
    Object.assign($ctrl, data);

    /*---------------------------------------*/
    /* If the contract date is not set,      */
    /* return to the login page (BOOKS-418). */
    /*---------------------------------------*/
    if (!angular.isDate(AppData.setupData.org.contractDate))
      AppServices.Alert.error(
        'Invalid Contract Date',
        '<p>Contract Start Date<br>is not set properly for this organization.</p>' +
          '<p>Contact Gray Step support for assistance.</p>'
      ).then(() => (location = '/'));
    /*----------------------------------------------------------------*/
    /* Watch for `$location.path()` changes to switch the page views. */
    /*----------------------------------------------------------------*/ else
      $rootScope.$watch(
        function () {
          return $location.path();
        },
        $injector.invoke(handlePageChange, null, $svcs)
      );

    BusyScreen.setBusy(false);
  });
}

/*****************************************************************************
 * init$ctrl */
/**
 * $ctrl property initializer.
 *
 * @param  $svcs  Properties to pass with `$injector.invoke()`.
 *****************************************************************************/
/* @ngInject */ function init$ctrl($svcs, $ctrl, $injector, AppConfig) {
  Object.assign($ctrl, {
    /*------------*/
    /* Properties */
    /*------------*/
    pageIndex: NaN,
    pageViews: AppConfig.pageViews.map(function (pv) {
      return new PageView(pv);
    }),

    /*-----------*/
    /* Functions */
    /*-----------*/
    addValidator: $injector.invoke(addValidator, null, $svcs),
    buildJournalEntries: $injector.invoke(buildJournalEntries, null, $svcs),
    changePage: $injector.invoke(changePage, null, $svcs),
    finishSetup: $injector.invoke(finishSetup, null, $svcs),
    nextPage: $injector.invoke(nextPage, null, $svcs),
    saveAndExit: $injector.invoke(saveAndExit, null, $svcs),
    saveSetup: $injector.invoke(saveSetup, null, $svcs),
    showHelp: $injector.invoke(showHelp, null, $svcs),

    previousPage: function () {
      $ctrl.changePage(-1);
    }
  });

  return Object.defineProperties($ctrl, {
    /*-----------*/
    /* Accessors */
    /*-----------*/
    currentView: {
      get: function () {
        return $ctrl.pageViews[$ctrl.pageIndex];
      }
    },
    isFirstPage: {
      get: function () {
        return $ctrl.pageIndex == 0;
      }
    },
    isLastPage: {
      get: function () {
        return $ctrl.pageIndex == $ctrl.pageViews.length - 1;
      }
    }
  });
}

/***************************************************************************
 * $ctrl.addValidator */
/**
 * Called by the `onLoad` handler of a page view to add a page validator.
 * Validators return `true` or a resolved promise if validation is successful.
 *
 * @param {number}   $index     Index of the view (ng-repeat `$index`).
 * @param {function} $validator Validator method to call.
 ***************************************************************************/
/* @ngInject */ function addValidator($ctrl) {
  return function ($index, $validator) {
    $ctrl.pageViews[$index].validator = $validator;
  };
}

/*******************************************************************************
 * $ctrl.buildJournalEntries */
/**
 * Creates account opening balance journal entries for Tax Year, Fiscal Year,
 * and Opening Date and saves the data to the server to complete the system setup.
 *
 * For each `JournalItem`, the account opening balance amount is the full balance
 * forward for the oldest balance. However, for the later entries, the line item
 * reflects only the change from the earlier balance. For instance, if the Tax
 * Year starts earlier than the Fiscal Year, the Tax Year item amount is the Tax
 * Year opening balance and the Fiscal Year item amount is the difference between
 * the Fiscal Year opening balance and the Tax Year opening balance.
 *
 * The opening balance total of all Balance Sheet accounts is offset by the Fund
 * Balance account opening balance which is automatically calculated.
 *
 * If the Opening Date does not start the Fiscal Year or the Tax Year, Income and
 * Expense balances have been provided by the user. These amounts are distributed
 * as the Change in Fund Balance (Net Income) for the FY/TY period for which they
 * were entered. If the user chose to adjust the Fund Balance rather than make
 * sure all balances were correct, a Fund Balance adjustment is added.
 *******************************************************************************/
/* @ngInject */ function buildJournalEntries(AppData, Data) {
  return function () {
    var tyEnd = new Date(
      AppData.tyStartDate.getFullYear(),
      AppData.tyStartDate.getMonth(),
      AppData.tyStartDate.getDate() - 1
    );
    var fyEnd = new Date(
      AppData.fyStartDate.getFullYear(),
      AppData.fyStartDate.getMonth(),
      AppData.fyStartDate.getDate() - 1
    );
    var cyEnd = new Date(
      AppData.orgInfo.openDate.getFullYear(),
      AppData.orgInfo.openDate.getMonth(),
      AppData.orgInfo.openDate.getDate() - 1
    );
    var fbAcct = AppData.accounts.filter(function (fbAcct) {
      return fbAcct.isFundBalance;
    })[0];
    var jeTaxYear = new Data.SetupJournal(
      tyEnd,
      'Tax Year ' + tyEnd.getFullYear() + ' Balance Forward'
    );
    var jeFiscalYear = new Data.SetupJournal(
      fyEnd,
      'Fiscal Year ' + fyEnd.getFullYear() + ' Balance Forward'
    );
    var jeOpenDate = new Data.SetupJournal(cyEnd, 'Opening Balance');

    /*-----------------------------------------------*/
    /* Add Journal Items for Balance Sheet accounts. */
    /*-----------------------------------------------*/
    AppData.bsAccounts.forEach(function (acct) {
      /*--------------------------------------*/
      /* Add FY/TY balance forward entries if */
      /* they don't start on the open date.   */
      /*--------------------------------------*/
      if (AppData.needFY)
        jeFiscalYear.add(
          acct,
          AppData.needTY && AppData.fyLater ? acct.tyOpeningBalance : 0,
          acct.fyOpeningBalance
        );

      if (AppData.needTY)
        jeTaxYear.add(
          acct,
          AppData.needFY && !AppData.fyLater ? acct.fyOpeningBalance : 0,
          acct.tyOpeningBalance
        );

      /*-----------------------------------------------*/
      /* Previous amount for opening date is zero      */
      /* unless we need fiscal year, tax year or both. */
      /*-----------------------------------------------*/
      var previous = 0;

      if (AppData.needFY && AppData.needTY)
        previous = AppData.fyLater
          ? acct.fyOpeningBalance
          : acct.tyOpeningBalance;
      else if (AppData.needFY) previous = acct.fyOpeningBalance;
      else if (AppData.needTY) previous = acct.tyOpeningBalance;

      /*---------------------------------------*/
      /* Set the balance for the opening date. */
      /*---------------------------------------*/
      if (!acct.isBankAccount)
        jeOpenDate.add(acct, previous, acct.currentBalance);
      /*---------------------------------------------------------------------------------*/
      /* For a Bank Account, the outstanding items will be entered against Fund          */
      /* Balance so we use the last statement balance for the account and make an        */
      /* adjustment to Fund Balance to compensate.                                       */
      /*                                                                                 */
      /* TODO: In the event the user has not correctly dated the outstanding items, it's */
      /* TODO: possible the supplied opening balance is not correct and will not report  */
      /* TODO: correctly. This is a corner case it may not be worth checking for but it  */
      /* TODO: is possible to validate outstanding item dates against period dates.      */
      /*---------------------------------------------------------------------------------*/ else {
        jeOpenDate.add(acct, previous, acct.lastStmtBalance);
        jeOpenDate.add(fbAcct, acct.currentBalance, acct.lastStmtBalance);
      }
    });

    /*----------------------------------------------------*/
    /* Add journal items for Income / Expense accounts.   */
    /* This is distribution of Net Income which for which */
    /* the opening balance is always 0 for the period.    */
    /*----------------------------------------------------*/
    AppData.ieAccounts.forEach(function (acct) {
      addCurrentBalances(acct, acct.fyCurrentBalance, acct.tyCurrentBalance);
    });

    /*---------------------------------------------------------------------*/
    /* For the earliest period entry, calculate the Fund Balance (retained */
    /* earnings) opening balance to offset the bank statement accounts.    */
    /*---------------------------------------------------------------------*/
    if (AppData.needFY && AppData.needTY) {
      if (AppData.fyLater)
        jeTaxYear.add(fbAcct, 0, AppData.tyCalcFundBalance);
      else jeFiscalYear.add(fbAcct, 0, AppData.fyCalcFundBalance);
    } else if (AppData.needFY)
      jeFiscalYear.add(fbAcct, 0, AppData.fyCalcFundBalance);
    else if (AppData.needTY)
      jeTaxYear.add(fbAcct, 0, AppData.tyCalcFundBalance);
    else jeOpenDate.add(fbAcct, 0, AppData.openingFundBalance);

    /*------------------------------------------------------*/
    /* If the user decided to adjust Fund Balance instead   */
    /* of making their books accurate, make the adjustment. */
    /*------------------------------------------------------*/
    addCurrentBalances(
      fbAcct,
      AppData.fyFundBalanceAdj,
      AppData.tyFundBalanceAdj
    );

    /*---------------------------------------------*/
    /* Add Journal Entries to setup data in proper */
    /* date order. Exclude entries with no items.  */
    /*---------------------------------------------*/
    var entries = (
      AppData.fyLater ? [jeTaxYear, jeFiscalYear] : [jeFiscalYear, jeTaxYear]
    ).concat([jeOpenDate]);
    AppData.setupData.entries = entries.filter(function (je) {
      return je.totalDebits > 0;
    });

    /****************************************************************************
     * Function: addCurrentBalances
     *
     * Adds current balance (Net Income) journal items.
     ****************************************************************************/
    function addCurrentBalances(acct, fyCurrentBalance, tyCurrentBalance) {
      /*-------------------------------------------------------------------*/
      /* If there is a Fiscal Year and a Tax Year, the balance forward for */
      /* the later period is earlier period balance less the later period  */
      /* balance. Open date balance forward is later period balance.       */
      /*-------------------------------------------------------------------*/
      if (AppData.needFY && AppData.needTY)
        if (AppData.fyLater) {
          jeFiscalYear.add(acct, 0, tyCurrentBalance - fyCurrentBalance);
          jeOpenDate.add(acct, 0, fyCurrentBalance);
        } else {
          jeTaxYear.add(acct, 0, fyCurrentBalance - tyCurrentBalance);
          jeOpenDate.add(acct, 0, tyCurrentBalance);
        }
      /*------------------------------------------*/
      /* Otherwise, set the opening date balance. */
      /*------------------------------------------*/ else if (AppData.needFY)
        jeOpenDate.add(acct, 0, fyCurrentBalance);
      else if (AppData.needTY) jeOpenDate.add(acct, 0, tyCurrentBalance);
    }
  };
}

/***************************************************************************
 * $ctrl.changePage */
/**
 * Changes the current page view by `delta`. Called recursively until the new
 * view has no `condition` or `condition` returns `true`. `$location.path()`
 * is set to the view ID of the new view and page change handler will change
 * the page view.
 *
 * @param  delta  Value to add to current view index.
 * @param  index  Current page index for recursive calls.
 ***************************************************************************/
/* @ngInject */ function changePage($ctrl, $injector, $location) {
  return function (delta, index) {
    var newIndex =
      ((angular.isUndefined(index) ? $ctrl.pageIndex : index) || 0) +
      (delta || 0);
    var newView = $ctrl.pageViews[newIndex];

    /*------------------------------------------------------------------*/
    /* If there is a `condition` for this view, invoke the condition    */
    /* method and move to the next view if it fails.                    */
    /*                                                                  */
    /* If `delta` is 0 and `condition` fails, the user tried to load to */
    /* page they can't get to. Default to 1 to prevent infinite loop.   */
    /*------------------------------------------------------------------*/
    if (
      newView.condition &&
      !$injector.invoke(newView.condition, null, { $ctrl: $ctrl })
    )
      return $ctrl.changePage((delta || 0) < 0 ? -1 : 1, newIndex);

    /*-------------------------------------------*/
    /* Set the view id in the location bar. Page */
    /* change handler will change the page view. */
    /*-------------------------------------------*/
    $location.path(newView.id);
  };
}

/*******************************************************************************
 * $ctrl.finishSetup */
/**
 * Finish button handler. Creates the setup journal entries, completes setup,
 * and loads the welcome page.
 *******************************************************************************/
/* @ngInject */ function finishSetup(
  $ctrl,
  $q,
  AppData,
  AppServices,
  BusyScreen,
  SERVER,
  Session,
  SetupServices
) {
  return function () {
    AppServices.Alert.confirm(
      'Finish Setup',
      '<p>Please be certain everything is correct.<br>' +
        'You cannot return to this process.<p>' +
        "<p>Are you sure you're ready to Complete the Setup?</p>",
      'Yes'
    ).then(function () {
      var reconfirm =
        (AppData.needFY && AppData.fyFundBalanceAdj !== 0) ||
        (AppData.needFY && AppData.fyFundBalanceAdj !== 0);
      var promise = !reconfirm
        ? $q.resolve()
        : AppServices.Alert.reverseConfirm(
            'Out of Balance',
            '<p>You have chosen to adjust <b>Fund Balance</b><br>' +
              'to bring your books into balance.</p>' +
              '<p>This is your final opportunity<br/>to correct your opening balances.</p>' +
              '<p>Are you sure you want to proceed?</p>',
            'No',
            'Yes'
          );
      promise.then(function () {
        $ctrl.buildJournalEntries();

        BusyScreen.setBusy(true);
        SetupServices.completeSetup(AppData.setupData).then(
          function () {
            BusyScreen.setBusy(false);
            AppServices.Alert.success(
              'Setup Complete',
              '<p>Setup is Complete!</p><p>Click Ok to load the Menu.</p>'
            ).then(function () {
              location = Session.getSessionURL(
                SERVER.BooksWebConstants.URL_WELCOME
              );
            });
          },
          function (resp) {
            console.log('resp', resp);
            if (!resp.parsedResponse.message())
              AppServices.serverErrorHandler;
            else {
              AppServices.Alert.error(resp.parsedResponse.message());
              BusyScreen.setBusy(false);
            }
          }
        );
      });
    });
  };
}

/*****************************************************************************
 * handlePageChange */
/**
 * `$location.path()` change handler. Changes the page view to the view ID in
 * the new path. Help dialog is shown if this is the first time to the page.
 *****************************************************************************/
/* @ngInject */ function handlePageChange($ctrl) {
  return function (newLocation) {
    /*----------------------------------------------------------*/
    /* Load the correct view from the path in the location bar. */
    /*----------------------------------------------------------*/
    var newPageID = newLocation.replace(/^\//, '');
    $ctrl.pageIndex = $ctrl.pageViews.reduce(function (d, e, i) {
      return newPageID == e.id ? i : d;
    }, 0);

    /*------------------------------------------------------*/
    /* Load any properties to the controller for this view. */
    /*------------------------------------------------------*/
    if ($ctrl.currentView.$ctrl)
      Object.assign($ctrl, $ctrl.currentView.$ctrl);

    /*------------------------------------------------*/
    /* Show the view help if this is the first visit. */
    /*------------------------------------------------*/
    if (!$ctrl.currentView.visited)
      $ctrl.showHelp().then(function () {
        $ctrl.currentView.visited = true;
      });
  };
}

/***************************************************************************
 * $ctrl.nextPage */
/**
 * Next button handler. Validates the input on the current page and changes
 * the page view if validation is successful.
 ***************************************************************************/
/* @ngInject */ function nextPage($ctrl, $q, AppData) {
  return function () {
    /*-----------------------------------------*/
    /* Call the page validator, if any.        */
    /* Validators return boolean or a promise. */
    /*-----------------------------------------*/
    (angular.isFunction($ctrl.currentView.validator)
      ? $q.when($ctrl.currentView.validator())
      : $q.resolve()
    ).then(function (valid) {
      if (valid !== false) {
        /*------------------------------------------------------------------*/
        /* Save current settings and move to the next page or finish setup. */
        /*------------------------------------------------------------------*/
        $ctrl.saveSetup().then(function () {
          if (!$ctrl.isLastPage) $ctrl.changePage(1);
          else $ctrl.finishSetup();
        });
      }
    });
  };
}

/***************************************************************************
 * $ctrl.saveAndExit */
/**
 * Save button handler. Saves setup information to the server and loads
 * the login page.
 ***************************************************************************/
/* @ngInject */ function saveAndExit($ctrl, AppServices) {
  return function () {
    AppServices.Alert.confirm(
      'Save and Exit',
      '<p>Your information will be saved and<br/>you can resume Setup at a later time.</p>' +
        '<p>Are you sure you want to exit the Setup?',
      'Yes'
    ).then(function () {
      $ctrl.saveSetup().then(function () {
        location = '/';
      });
    });
  };
}

/***************************************************************************
 * $ctrl.saveSetup */
/**
 * Saves setup information to a file on the server.
 *
 * @return {promise} Resolved when save is complete.
 ***************************************************************************/
/* @ngInject */ function saveSetup(
  $q,
  AppData,
  AppServices,
  BusyScreen,
  SetupServices
) {
  return function () {
    BusyScreen.setBusy(true);
    return SetupServices.saveSetup(AppData.setupData).then(function () {
      BusyScreen.setBusy(false);
      return $q.resolve();
    }, AppServices.serverErrorHandler);
  };
}

/***************************************************************************
 * $ctrl.showHelp */
/**
 * Help button handler. Shows the help content for the current page view.
 *
 * @return {promise} Resolved when dialog is closed.
 ***************************************************************************/
/* @ngInject */ function showHelp($ctrl, AppConfig, HelpDialog) {
  return function () {
    return HelpDialog.show(AppConfig.helpPages[$ctrl.currentView.id]).closed;
  };
}

/*****************************************************************************
 * PageView */
/**
 * PageView object constructor.
 *
 * @param  pv  Page view settings from AppConfig.
 *****************************************************************************/
function PageView(pv) {
  Object.seal(
    Object.assign(this, pv, {
      validator: null, // Validator to call on Next button click.
      visited: false // Set after page has been visited.
    })
  );
}
